import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  /** quantity* kan tijdens morph even ontbreken voor de MutationObserver op refs */
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {Element | null} */
  #scrollVisibilityTarget = null;

  /** @type {MutationObserver | null} */
  #shippingDeliveryObserver = null;

  /** @type {ResizeObserver | null} */
  #shippingDeliveryResizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #deliverySyncTimer = null;

  /** Opgeslagen theme-vinkje (SVG) voor terugzetten als PDP-blok geen zichtbaar icoon heeft */
  /** @type {string | undefined} */
  #defaultDeliveryIconMarkup = undefined;

  /** `.shopify-section` waarop we mutaties volgen (incl. vervangen van `.shipping-delivery-block`). */
  /** @type {Element | undefined} */
  #deliveryMutationSection = undefined;

  /** Huidig `.shipping-delivery-block` voor ResizeObserver (tekst/layout-wijzigingen). */
  /** @type {Element | undefined} */
  #resizeObservedDeliveryBlock = undefined;

  /** Signatuur van het laatst toegepaste leverings-icoon, om redundante DOM-writes (en daarmee
   * self-triggering observer-loops) te voorkomen. */
  /** @type {string | undefined} */
  #appliedDeliveryIconSignature = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    this.#scheduleDeliverySync();
    requestAnimationFrame(() => {
      this.#scheduleDeliverySync();
      this.#ensureShippingDeliveryObserver();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#shippingDeliveryObserver?.disconnect();
    this.#shippingDeliveryObserver = null;
    this.#deliveryMutationSection = undefined;
    this.#shippingDeliveryResizeObserver?.disconnect();
    this.#shippingDeliveryResizeObserver = null;
    this.#resizeObservedDeliveryBlock = undefined;
    if (this.#deliverySyncTimer) {
      clearTimeout(this.#deliverySyncTimer);
      this.#deliverySyncTimer = null;
    }
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Na morph/ref-sync quantity-label opnieuw zetten.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#updateButtonText();
    this.#scheduleDeliverySync();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer');
    if (!footer) return;

    const addToCartInForm = productForm.querySelector('[ref="addToCartButton"]');
    const observeTarget = /** @type {Element} */ (addToCartInForm ?? buyButtonsBlock);
    this.#scrollVisibilityTarget = observeTarget;

    // Observer for main ATC visibility (prefer the real button over the whole buy-buttons block)
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        // Only show sticky bar if the observed target has been scrolled past (above viewport)
        if (!entry.isIntersecting && !this.#isStuck) {
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#showStickyBar();
          }
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      },
      {
        root: null,
        rootMargin: '0px 0px -12px 0px',
        threshold: 0,
      }
    );

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const scrollTarget = this.#scrollVisibilityTarget;
          if (!scrollTarget) return;
          const rect = scrollTarget.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(observeTarget);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = addToCartInForm;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#scheduleDeliverySync();
    this.#ensureShippingDeliveryObserver();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Clears variant id when geen beschikbare variant (variantregel in sticky wordt niet meer getoond).
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    if (!addToCartButton || !quantityDisplay || !quantityNumber) return;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  /**
   * PDP delivery-app: `.shipping-delivery-block` met o.a. `.delivery-message-pill` (zie theme custom_css op product template).
   * Alleen zichtbare levertijdtekst + optioneel vinkje; theme verbergt vaak `.delivery-message-pill__icon-wrapper` op de PDP.
   */
  #scheduleDeliverySync() {
    if (this.#deliverySyncTimer) clearTimeout(this.#deliverySyncTimer);
    this.#deliverySyncTimer = setTimeout(() => {
      this.#deliverySyncTimer = null;
      this.#syncDeliveryFromShippingBlock();
    }, 75);
  }

  /**
   * Volgt levertijd-updates: MutationObserver op de hele productsectie (app kan blok vervangen),
   * plus ResizeObserver op `.shipping-delivery-block` voor tekst-/layoutwijzigingen.
   */
  #ensureShippingDeliveryObserver() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    if (this.#deliveryMutationSection !== section || !this.#shippingDeliveryObserver) {
      this.#shippingDeliveryObserver?.disconnect();
      this.#shippingDeliveryObserver = new MutationObserver((records) => {
        /* Negeer mutaties die wíj zelf in de sticky-bar veroorzaken (textContent/icon/aria-hidden):
         * anders triggeren onze eigen writes de observer → sync → write → ... een eindeloze loop
         * (~22 forced reflows/sec). De `.shipping-delivery-block` zit buiten `this`, dus echte
         * app-wijzigingen passeren wel. */
        if (records.every((record) => this.contains(record.target))) return;
        this.#scheduleDeliverySync();
      });
      this.#shippingDeliveryObserver.observe(section, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
      this.#deliveryMutationSection = section;
    }

    const block = section.querySelector('.shipping-delivery-block');
    if (block) {
      if (this.#resizeObservedDeliveryBlock !== block || !this.#shippingDeliveryResizeObserver) {
        this.#shippingDeliveryResizeObserver?.disconnect();
        this.#shippingDeliveryResizeObserver = new ResizeObserver(() => this.#scheduleDeliverySync());
        this.#shippingDeliveryResizeObserver.observe(block);
        this.#resizeObservedDeliveryBlock = block;
      }
    } else {
      this.#shippingDeliveryResizeObserver?.disconnect();
      this.#shippingDeliveryResizeObserver = null;
      this.#resizeObservedDeliveryBlock = undefined;
    }
  }

  /**
   * @param {Element | null} el
   */
  #isDeliveryIconVisible(el) {
    if (!(el instanceof Element)) return false;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    return true;
  }

  /**
   * @param {Element} source
   * @param {Element | null | undefined} pill
   */
  #extractDeliveryText(source, pill) {
    if (pill instanceof HTMLElement) {
      const clone = /** @type {HTMLElement} */ (pill.cloneNode(true));
      clone.querySelectorAll('.delivery-message-pill__icon-wrapper, [class*="icon-wrapper"]').forEach((el) => el.remove());
      const t = clone.innerText?.replace(/\s+/g, ' ').trim() ?? '';
      if (t) return t;
    }
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    clone.querySelectorAll('script, style, template, [hidden]').forEach((el) => el.remove());
    return clone.innerText?.replace(/\s+/g, ' ').trim() ?? '';
  }

  #syncDeliveryFromShippingBlock() {
    const stickyDelivery = this.querySelector('.sticky-add-to-cart__delivery[data-delivery-source="shipping-block"]');
    if (!stickyDelivery) return;

    const textEl = stickyDelivery.querySelector('.sticky-add-to-cart__delivery-text');
    const iconWrap = stickyDelivery.querySelector('.sticky-add-to-cart__delivery-icon');
    if (!textEl || !iconWrap) return;

    /* Na morph: opnieuw baseline theme-icoon vastleggen (niet overschrijven met eerder gekloonde app-SVG). */
    if (!iconWrap.querySelector('[data-from-shipping-block="true"]')) {
      this.#defaultDeliveryIconMarkup = iconWrap.innerHTML;
      /* DOM is door morph teruggezet → cached signatuur is stale, opnieuw evalueren. */
      this.#appliedDeliveryIconSignature = undefined;
    }

    /* Altijd koppelen zodra de sticky-leverregel bestaat: sectie mutaties (blok dat later mount) + resize op blok. */
    this.#ensureShippingDeliveryObserver();

    const section = this.closest('.shopify-section');
    const source = section?.querySelector('.shipping-delivery-block');
    if (!source) {
      return;
    }

    const pill =
      source.querySelector('.delivery-message-pill') ||
      source.querySelector('[class*="delivery-message-pill"]');

    const text = this.#extractDeliveryText(source, pill);
    /* Alleen schrijven als de tekst echt wijzigt: een no-op write zou de MutationObserver
     * triggeren en de sync-loop levend houden. */
    if (text && textEl.textContent !== text) {
      textEl.textContent = text;
    }

    const iconWrapper =
      (pill instanceof Element &&
        (pill.querySelector('.delivery-message-pill__icon-wrapper') ||
          pill.querySelector('[class*="delivery-message-pill__icon-wrapper"]') ||
          pill.querySelector('[class*="icon-wrapper"]'))) ||
      null;

    const toClone =
      pill && iconWrapper ? iconWrapper.querySelector('svg, img') || iconWrapper.firstElementChild : null;

    if (toClone && this.#isDeliveryIconVisible(iconWrapper)) {
      /* Signatuur eerst vergelijken: identiek icoon → geen DOM-write (en geen observer-trigger). */
      const signature = `block:${toClone.outerHTML}`;
      if (this.#appliedDeliveryIconSignature !== signature) {
        const cloned = toClone.cloneNode(true);
        if (cloned instanceof Element) {
          cloned.setAttribute('data-from-shipping-block', 'true');
        }
        iconWrap.replaceChildren(cloned);
        iconWrap.setAttribute('aria-hidden', 'true');
        this.#appliedDeliveryIconSignature = signature;
      }
    } else if (this.#defaultDeliveryIconMarkup !== undefined && this.#appliedDeliveryIconSignature !== 'default') {
      iconWrap.innerHTML = this.#defaultDeliveryIconMarkup;
      iconWrap.setAttribute('aria-hidden', 'true');
      this.#appliedDeliveryIconSignature = 'default';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
